/* GoAgain — theme.js. No dependencies. Progressive enhancement: every form works without this file. */
(() => {
  'use strict';

  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));
  const log = (...a) => { if (window.GoAgainDebug) console.debug('[goagain]', ...a); };

  // Give the LCP image a paint opportunity before loading decorative font variants.
  const detailFonts = document.getElementById('anville-detail-fonts');
  if (detailFonts) {
    let fontsScheduled = false;
    const revealFonts = () => {
      if (fontsScheduled) return;
      fontsScheduled = true;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        document.head.appendChild(detailFonts.content.cloneNode(true));
        detailFonts.remove();
      }));
    };
    const heroImage = qs('.hero__gallery .gallery__slide img');
    if (!heroImage || heroImage.complete) revealFonts();
    else {
      heroImage.addEventListener('load', revealFonts, { once: true });
      heroImage.addEventListener('error', revealFonts, { once: true });
    }
  }

  /* ---------------- Buy box ---------------- */
  class BuyBox {
    constructor(root) {
      this.root = root;
      this.form = qs('[data-buybox-form]', root);
      this.idInput = qs('[data-variant-id]', root);
      this.submit = qs('[data-submit]', root);
      this.submitLabel = qs('[data-submit-label]', root);
      this.submitPrice = qs('[data-submit-price]', root);
      this.sizeError = qs('[data-size-error]', root);
      this.sizesFs = qs('.sizes', root);
      this.upsellBox = qs('[data-upsell-box]', root);
      this.upsellText = qs('[data-upsell-text]', root);
      this.upsellBtn = qs('[data-upsell-btn]', root);
      this.packIdx = Number(root.dataset.packIndex);
      this.sizeIdx = Number(root.dataset.sizeIndex);
      this.defaultLabel = this.submitLabel ? this.submitLabel.dataset.defaultLabel : '';

      try {
        this.variants = JSON.parse(qs('[data-variants]', root).textContent);
      } catch (e) {
        console.error('[goagain] variant JSON unreadable — falling back to no-JS form', e);
        return;
      }

      // JS is alive: hand control to the hidden id input, remove the no-JS select.
      qsa('.nojs-select', root).forEach((el) => el.remove());
      this.idInput.disabled = false;

      root.addEventListener('change', (e) => {
        if (e.target.matches('.pack__input, .size__input')) this.update(e.target);
      });
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      if (this.upsellBtn) this.upsellBtn.addEventListener('click', () => this.upgrade());

      this.update();
    }

    get pack() { const el = qs('.pack__input:checked', this.root); return el ? el.value : null; }
    get size() { const el = qs('.size__input:checked', this.root); return el ? el.value : null; }
    get packInput() { return qs('.pack__input:checked', this.root); }

    selectPack(value) {
      const el = qsa('.pack__input', this.root).find((i) => i.value === value);
      if (!el) return false;
      el.checked = true;
      this.update(el);
      return true;
    }

    find(pack, size) {
      return this.variants.find((v) =>
        (Number.isNaN(this.packIdx) || v.options[this.packIdx] === pack) &&
        (Number.isNaN(this.sizeIdx) || size === null || v.options[this.sizeIdx] === size));
    }

    update(changed) {
      const pack = this.pack;
      const size = this.size;
      const variant = this.find(pack, size);

      if (variant) this.idInput.value = variant.id;

      // Mark sizes unavailable for this pack.
      qsa('.size__input', this.root).forEach((input) => {
        const v = this.find(pack, input.value);
        input.disabled = !!v && !v.available;
      });

      // Button state
      const available = variant ? variant.available : false;
      if (size && !available) {
        this.submitLabel.textContent = this.submitLabel.dataset.soldOut || 'Sold out';
        this.submit.setAttribute('aria-disabled', 'true');
      } else {
        this.submitLabel.textContent = this.defaultLabel;
        this.submit.removeAttribute('aria-disabled');
      }
      const packInput = this.packInput;
      this.submitPrice.textContent = packInput ? packInput.dataset.price : '';

      // Upgrade nudge
      if (this.upsellBox) {
        const txt = packInput && packInput.dataset.upsell;
        this.upsellBox.hidden = !txt;
        if (txt) {
          this.upsellText.textContent = txt;
          this.upsellBtn.dataset.target = packInput.dataset.upsellTarget;
        }
      }

      if (changed && changed.matches('.size__input')) this.clearError();

      this.root.dispatchEvent(new CustomEvent('buybox:change', {
        bubbles: true,
        detail: { pack, size, variantId: variant && variant.id, price: packInput && packInput.dataset.price },
      }));
      log('update', { pack, size, variant });
    }

    upgrade() {
      const target = this.upsellBtn.dataset.target;
      if (target) this.selectPack(target);
    }

    showError() {
      if (!this.sizeError) return;
      this.sizeError.hidden = false;
      this.sizesFs.classList.add('sizes--invalid');
      const first = qs('.size__input:not(:disabled)', this.root);
      this.sizesFs.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (first) first.focus({ preventScroll: true });
    }

    clearError() {
      if (!this.sizeError) return;
      this.sizeError.hidden = true;
      this.sizesFs.classList.remove('sizes--invalid');
    }

    onSubmit(e) {
      if (this.sizesFs && !this.size) { e.preventDefault(); this.showError(); return; }
      if (this.submit.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
      const v = this.find(this.pack, this.size);
      if (!v) { e.preventDefault(); console.error('[goagain] no variant for', this.pack, this.size); return; }
      this.idInput.value = v.id;

      // Drawer mode: add via AJAX and open the cart drawer. Any failure falls back to the native POST.
      if (this.form.dataset.atcMode === 'drawer' && window.AnvCart) {
        e.preventDefault();
        this.submit.setAttribute('aria-busy', 'true');
        this.submit.disabled = true;
        window.AnvCart.add([{ id: v.id, quantity: 1 }])
          .then(() => window.AnvCart.open(this.submit))
          .catch((err) => {
            console.error('[anville] AJAX add failed, falling back to form post', err);
            HTMLFormElement.prototype.submit.call(this.form);
          })
          .finally(() => { this.submit.disabled = false; this.submit.removeAttribute('aria-busy'); });
        return;
      }

      // Prevent double submits; native POST continues.
      this.submit.setAttribute('aria-busy', 'true');
      setTimeout(() => { this.submit.disabled = true; }, 0);
    }
  }

  /* ---------------- Cart drawer ----------------
     Server-rendered via Section Rendering API: every mutation returns fresh drawer HTML from Shopify,
     so prices/totals are never computed client-side. */
  const AnvCart = (() => {
    const dialog = qs('[data-cart-drawer]');
    if (!dialog || typeof dialog.showModal !== 'function') return null;
    const SECTION = 'anv-cart-drawer';
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    let busy = false;
    let opener = null;

    const inner = () => qs('[data-cd-inner]', dialog);

    const swap = (sections) => {
      const html = sections && sections[SECTION];
      if (!html) throw new Error('section missing from response');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = qs('[data-cd-inner]', doc);
      if (!next) throw new Error('drawer markup missing');
      inner().replaceWith(next);
      const label = next.dataset.countLabel;
      qsa('[data-cart-count]').forEach((el) => { el.textContent = label; });
      document.dispatchEvent(new CustomEvent('anv:cart', { detail: { count: Number(next.dataset.count) } }));
      // Shipping protection never stays on its own.
      if (next.dataset.onlyInsurance === 'true' && next.dataset.insKey) {
        return change(next.dataset.insKey, 0);
      }
      return null;
    };

    const request = async (url, body) => {
      busy = true;
      dialog.setAttribute('aria-busy', 'true');
      const err = qs('[data-cd-error]', dialog);
      if (err) err.hidden = true;
      try {
        const res = await fetch(root + url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: SECTION, sections_url: location.pathname }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.description || data.message || `HTTP ${res.status}`);
        await swap(data.sections);
        return data;
      } catch (e) {
        const box = qs('[data-cd-error]', dialog);
        if (box) { box.textContent = e.message || box.dataset.fallback || 'Something went wrong.'; box.hidden = false; }
        throw e;
      } finally {
        busy = false;
        dialog.removeAttribute('aria-busy');
      }
    };

    const add = (items) => request('cart/add.js', { items });
    const change = (key, quantity) => request('cart/change.js', { id: key, quantity });

    const refresh = async () => {
      const res = await fetch(`${root}?sections=${SECTION}`);
      if (res.ok) await swap(await res.json());
    };

    const open = (from) => {
      opener = from || document.activeElement;
      if (!dialog.open) dialog.showModal();
      document.documentElement.classList.add('cd-lock');
    };
    const close = () => {
      dialog.close();
    };
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('cd-lock');
      if (opener && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
    });

    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) { close(); return; } // backdrop
      const t = e.target.closest('[data-cd-close], [data-cd-qty], [data-cd-add], [data-cd-scroll]');
      if (!t || busy) return;
      if (t.matches('[data-cd-close]')) {
        if (t.tagName === 'A' && t.getAttribute('href').includes('#')) { close(); return; }
        e.preventDefault(); close(); return;
      }
      if (t.matches('[data-cd-qty]')) {
        const key = t.closest('[data-key]').dataset.key;
        change(key, Number(t.dataset.cdQty)).catch(() => {});
        return;
      }
      if (t.matches('[data-cd-add]')) {
        t.disabled = true;
        add([{ id: Number(t.dataset.cdAdd), quantity: 1 }]).catch(() => { t.disabled = false; });
        return;
      }
      if (t.matches('[data-cd-scroll]')) {
        const track = qs('[data-cd-track]', dialog);
        if (track) track.scrollBy({ left: Number(t.dataset.cdScroll) * track.clientWidth, behavior: 'smooth' });
      }
    });

    dialog.addEventListener('change', (e) => {
      const sw = e.target.closest('[data-cd-insurance]');
      if (!sw) return;
      const key = inner().dataset.insKey;
      const p = sw.checked ? add([{ id: Number(sw.dataset.variant), quantity: 1 }]) : (key ? change(key, 0) : Promise.resolve());
      p.catch(() => { sw.checked = !sw.checked; });
    });

    document.addEventListener('click', (e) => {
      const a = e.target.closest('[data-cart-open]');
      if (!a) return;
      e.preventDefault();
      refresh().catch(() => {}).finally(() => open(a));
    });

    // Back/forward cache can show a stale cart.
    window.addEventListener('pageshow', (e) => { if (e.persisted) refresh().catch(() => {}); });

    return { add, change, open, close, refresh };
  })();
  window.AnvCart = AnvCart;

  const boxes = qsa('[data-buybox]').map((el) => new BuyBox(el)).filter((b) => b.variants);

  /* Deep link: ?pack=4%20Pairs#buy pre-selects in every buy box. */
  const urlPack = new URLSearchParams(location.search).get('pack');
  if (urlPack) boxes.forEach((b) => b.selectPack(urlPack));

  /* Links that pre-select a pack before jumping to #buy. */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-select-pack]');
    if (!a) return;
    const hero = boxes[0];
    if (hero && a.dataset.selectPack) hero.selectPack(a.dataset.selectPack);
  });

  /* ---------------- Sticky ATC ---------------- */
  const sticky = qs('[data-sticky-atc]');
  if (sticky && boxes.length && 'IntersectionObserver' in window) {
    const packEl = qs('[data-sticky-pack]', sticky);
    const priceEl = qs('[data-sticky-price]', sticky);
    const heroForm = boxes[0].form;
    const visible = new Set();
    let passedHero = false;

    const render = () => {
      const show = passedHero && visible.size === 0;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      qsa('a', sticky).forEach((a) => { a.tabIndex = show ? 0 : -1; });
    };
    const sync = () => { packEl.textContent = boxes[0].pack || ''; priceEl.textContent = boxes[0].packInput ? boxes[0].packInput.dataset.price : ''; };

    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) visible.add(en.target); else visible.delete(en.target);
        if (en.target === heroForm) passedHero = !en.isIntersecting && en.boundingClientRect.top < 0;
      });
      render();
    });
    boxes.forEach((b) => io.observe(b.form));
    boxes[0].root.addEventListener('buybox:change', sync);
    sticky.hidden = false;
    sync();
    render();
  }

  /* ---------------- Gallery dots ---------------- */
  qsa('[data-gallery]').forEach((g) => {
    const track = qs('[data-gallery-track]', g);
    const dotsWrap = qs('[data-gallery-dots]', g);
    const slides = qsa('.gallery__slide', track);
    if (slides.length < 2) { dotsWrap.remove(); return; }

    // Keep offscreen gallery images out of the initial network queue.
    const hydrateSlide = (slide) => {
      const template = qs('template[data-gallery-image]', slide);
      if (!template) return;
      const content = template.content.cloneNode(true);
      qsa('img', content).forEach((image) => { image.loading = 'eager'; image.fetchPriority = 'auto'; });
      template.replaceWith(content);
    };
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) { hydrateSlide(entry.target); imageObserver.unobserve(entry.target); }
        });
      }, { root: track, threshold: 0.01 });
      slides.forEach((slide) => imageObserver.observe(slide));
    } else {
      slides.forEach(hydrateSlide);
    }

    const dots = slides.map((s, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'gallery__dot';
      b.setAttribute('aria-label', `Show image ${i + 1}`);
      b.addEventListener('click', () => { hydrateSlide(s); track.scrollTo({ left: s.offsetLeft - track.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || 0), behavior: 'smooth' }); });
      dotsWrap.appendChild(b);
      return b;
    });
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) {
          const i = slides.indexOf(en.target);
          dots.forEach((d, j) => d.setAttribute('aria-current', String(i === j)));
        }
      });
    }, { root: track, threshold: 0.6 });
    slides.forEach((s) => io.observe(s));
    dots[0].setAttribute('aria-current', 'true');
  });

  /* ---------------- Dialogs ---------------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dialog-open]');
    if (opener) {
      const d = document.getElementById(opener.dataset.dialogOpen);
      if (d && typeof d.showModal === 'function') d.showModal();
      return;
    }
    if (e.target.closest('[data-dialog-close]')) { e.target.closest('dialog').close(); return; }
    if (e.target.tagName === 'DIALOG') e.target.close(); // backdrop click
  });

  /* Restore buttons on bfcache back-navigation from checkout. */
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) qsa('[data-submit]').forEach((b) => { b.disabled = false; b.removeAttribute('aria-busy'); });
  });
})();
